<template>
  <sistema>
    <container>
      <template v-slot:header>
        <h3>Dashboard</h3>
      </template>
      <template v-slot:body>
        <div>
          <div class="comparativo-faturamento">
            <div class="comparativo-header">
              <h4>Faturamento — Comparativo</h4>
              <div class="comparativo-filtros">
                <Dropdown
                  v-model="state.periodoTipo"
                  :options="periodoOptions"
                  optionValue="value"
                  optionLabel="label"
                  @change="handleTipoChange"
                />
                <Dropdown
                  v-if="state.periodoTipo !== 'ANUAL'"
                  v-model="state.referencia"
                  :options="opcoesInstancia"
                  optionValue="value"
                  optionLabel="label"
                  @change="executeBuscaComparativoFaturamento"
                />
              </div>
            </div>
            <div v-if="state.chartHasData" class="comparativo-chart">
              <Chart
                type="line"
                :data="state.chartData"
                :options="state.chartOptions"
              />
            </div>
            <p v-else-if="state.chartLoaded" class="comparativo-vazio">
              Ainda não há dados coletados para este período ou para o mesmo
              período do ano anterior.
            </p>
          </div>
        </div>
      </template>
    </container>
  </sistema>
</template>

<script lang="ts">
import { useAuth, PermissionPayload } from "../../modules/auth";
import {
  useFaturamentoComparativoService,
  FaturamentoDiaPayload,
  PeriodoComparativoTipo
} from "../../modules/business/cliente/faturamentoComparativoBusiness";
import { defineComponent, reactive, computed, onMounted } from "vue";
import { useToast } from "../../modules/message";

type Granularidade = "day" | "week" | "month";

interface DashboardState {
  chartData: unknown | null;
  chartOptions: unknown | null;
  chartHasData: boolean;
  chartLoaded: boolean;
  periodoTipo: PeriodoComparativoTipo;
  // Data (YYYY-MM-DD) dentro do mês/trimestre/semestre escolhido — o backend
  // usa ela só pra achar em qual período cai, não precisa ser o dia 1.
  referencia: string;
}

const periodoOptions: Array<{
  label: string;
  value: PeriodoComparativoTipo;
}> = [
  { label: "Mensal", value: "MENSAL" },
  { label: "Trimestral", value: "TRIMESTRAL" },
  { label: "Semestral", value: "SEMESTRAL" },
  { label: "Anual", value: "ANUAL" }
];

const MESES_ABREV = [
  "Jan",
  "Fev",
  "Mar",
  "Abr",
  "Mai",
  "Jun",
  "Jul",
  "Ago",
  "Set",
  "Out",
  "Nov",
  "Dez"
];

const MESES_NOMES = [
  "Janeiro",
  "Fevereiro",
  "Março",
  "Abril",
  "Maio",
  "Junho",
  "Julho",
  "Agosto",
  "Setembro",
  "Outubro",
  "Novembro",
  "Dezembro"
];

const hojeISO = (): string => new Date().toISOString().substring(0, 10);

const dataDoMes = (ano: number, mesIndex: number): string => {
  // mesIndex: 0-based (0 = Janeiro)
  return `${ano}-${String(mesIndex + 1).padStart(2, "0")}-01`;
};

const referenciaAtual = (tipo: PeriodoComparativoTipo): string => {
  const agora = new Date();
  const ano = agora.getFullYear();
  const mes = agora.getMonth();
  if (tipo === "TRIMESTRAL") {
    return dataDoMes(ano, Math.floor(mes / 3) * 3);
  }
  if (tipo === "SEMESTRAL") {
    return dataDoMes(ano, Math.floor(mes / 6) * 6);
  }
  if (tipo === "ANUAL") {
    return hojeISO();
  }
  return dataDoMes(ano, mes);
};

export default defineComponent({
  setup() {
    const state = reactive<DashboardState>({
      chartData: null,
      chartHasData: false,
      chartLoaded: false,
      periodoTipo: "MENSAL",
      referencia: referenciaAtual("MENSAL"),
      chartOptions: {
        responsive: true,
        maintainAspectRatio: false,
        interaction: {
          mode: "index",
          intersect: false
        },
        plugins: {
          legend: {
            position: "bottom",
            labels: {
              usePointStyle: true,
              boxWidth: 8,
              padding: 20
            }
          },
          tooltip: {
            padding: 10,
            cornerRadius: 6,
            boxPadding: 4
          }
        },
        elements: {
          line: {
            spanGaps: false,
            borderWidth: 2
          },
          point: {
            radius: 0,
            hoverRadius: 5,
            hoverBorderWidth: 2
          }
        },
        scales: {
          x: {
            grid: {
              display: false
            },
            ticks: {
              maxRotation: 0,
              autoSkip: true,
              maxTicksLimit: 8
            }
          },
          y: {
            beginAtZero: true,
            grid: {
              color: "rgba(0, 0, 0, 0.05)"
            }
          }
        }
      }
    });

    const amIAllowed = (
      authority: string,
      permissionInfo?: PermissionPayload | string | null
    ) => {
      return useAuth().amIAllowed(authority, permissionInfo);
    };

    const opcoesInstancia = computed(() => {
      const ano = new Date().getFullYear();
      if (state.periodoTipo === "TRIMESTRAL") {
        return [0, 1, 2, 3].map((t) => ({
          label: `${t + 1}º Trimestre`,
          value: dataDoMes(ano, t * 3)
        }));
      }
      if (state.periodoTipo === "SEMESTRAL") {
        return [0, 1].map((s) => ({
          label: `${s + 1}º Semestre`,
          value: dataDoMes(ano, s * 6)
        }));
      }
      // MENSAL (padrão)
      return MESES_NOMES.map((nome, i) => ({
        label: nome,
        value: dataDoMes(ano, i)
      }));
    });

    // Índice (1-based) de "dataPonto" dentro do período que começa em
    // "inicioPeriodo", na granularidade dada pelo backend — substitui a lógica
    // antiga de "dia do mês", que só fazia sentido pro caso Mensal.
    const diffEmDias = (a: Date, b: Date): number => {
      return Math.round((a.getTime() - b.getTime()) / (1000 * 60 * 60 * 24));
    };

    const indiceNoPeriodo = (
      dataPontoStr?: string | null,
      inicioPeriodoStr?: string | null,
      granularidade?: Granularidade | null
    ): number | null => {
      if (!dataPontoStr || !inicioPeriodoStr || !granularidade) {
        return null;
      }
      const dataPonto = new Date(dataPontoStr);
      const inicioPeriodo = new Date(inicioPeriodoStr);
      if (isNaN(dataPonto.getTime()) || isNaN(inicioPeriodo.getTime())) {
        return null;
      }
      if (granularidade === "month") {
        const meses =
          (dataPonto.getUTCFullYear() - inicioPeriodo.getUTCFullYear()) * 12 +
          (dataPonto.getUTCMonth() - inicioPeriodo.getUTCMonth());
        return meses + 1;
      }
      const dias = diffEmDias(dataPonto, inicioPeriodo);
      if (granularidade === "week") {
        return Math.floor(dias / 7) + 1;
      }
      return dias + 1;
    };

    const montarSerieAlinhada = (
      serie: FaturamentoDiaPayload[],
      inicioPeriodo: string | null | undefined,
      granularidade: Granularidade | null | undefined,
      totalBuckets: number
    ): (number | null)[] => {
      const alinhada: (number | null)[] = new Array(totalBuckets).fill(null);
      serie.forEach((s) => {
        const indice = indiceNoPeriodo(s.dia, inicioPeriodo, granularidade);
        if (indice !== null && indice >= 1 && indice <= totalBuckets) {
          alinhada[indice - 1] = s.quantidade ?? null;
        }
      });
      return alinhada;
    };

    const montarLabels = (
      totalBuckets: number,
      inicioPeriodo: string | null | undefined,
      granularidade: Granularidade | null | undefined
    ): string[] => {
      const labels: string[] = [];
      if (granularidade === "month" && inicioPeriodo) {
        const inicio = new Date(inicioPeriodo);
        for (let i = 0; i < totalBuckets; i++) {
          labels.push(MESES_ABREV[(inicio.getUTCMonth() + i) % 12]);
        }
        return labels;
      }
      const prefixo = granularidade === "week" ? "Semana" : "Dia";
      for (let i = 0; i < totalBuckets; i++) {
        labels.push(`${prefixo} ${i + 1}`);
      }
      return labels;
    };

    // Acumulado do período (bucket 1, 1+2, 1+2+3, ...), não o valor isolado de
    // cada bucket — é assim que "período atual x ano anterior" fica comparável.
    // Antes do primeiro dado real, fica null (sem linha); depois, mesmo buckets
    // sem incremento novo mantêm o total anterior. `indiceMaximoValido` corta o
    // acumulado em buckets futuros (que ainda não aconteceram) em vez de
    // arrastar o total antigo até o fim do período — usado só na série atual,
    // já que a do ano anterior não tem "futuro" (o período já passou por completo).
    const acumular = (
      valores: (number | null)[],
      indiceMaximoValido?: number
    ): (number | null)[] => {
      let soma = 0;
      let iniciou = false;
      return valores.map((v, i) => {
        if (indiceMaximoValido !== undefined && i + 1 > indiceMaximoValido) {
          return null;
        }
        if (v !== null) {
          soma += v;
          iniciou = true;
        }
        return iniciou ? soma : null;
      });
    };

    const executeBuscaComparativoFaturamento = async () => {
      try {
        const {
          data,
          getComparativoFaturamento
        } = useFaturamentoComparativoService();
        await getComparativoFaturamento(state.periodoTipo, state.referencia);
        const comparativo = data.value;
        const serieAtual: FaturamentoDiaPayload[] =
          comparativo?.serieAtual ?? [];
        const serieAnoAnterior: FaturamentoDiaPayload[] =
          comparativo?.serieAnoAnterior ?? [];
        const granularidade: Granularidade =
          comparativo?.granularidade ?? "day";
        const totalBuckets: number = comparativo?.totalBuckets ?? 1;
        const bucketAtual: number = comparativo?.bucketAtual ?? totalBuckets;

        state.chartHasData =
          serieAtual.length > 0 || serieAnoAnterior.length > 0;
        state.chartData = {
          labels: montarLabels(
            totalBuckets,
            comparativo?.inicioAtual,
            granularidade
          ),
          datasets: [
            {
              label: "Período Atual",
              data: acumular(
                montarSerieAlinhada(
                  serieAtual,
                  comparativo?.inicioAtual,
                  granularidade,
                  totalBuckets
                ),
                bucketAtual
              ),
              borderColor: "#16A34A",
              backgroundColor: "#16A34A",
              pointBackgroundColor: "#16A34A",
              fill: false,
              tension: 0.3
            },
            {
              label: "Mesmo Período Ano Anterior",
              data: acumular(
                montarSerieAlinhada(
                  serieAnoAnterior,
                  comparativo?.inicioAnoAnterior,
                  granularidade,
                  totalBuckets
                )
              ),
              borderColor: "#9CA3AF",
              backgroundColor: "#9CA3AF",
              pointBackgroundColor: "#9CA3AF",
              borderDash: [5, 5],
              fill: false,
              tension: 0.3
            }
          ]
        };
      } catch (error) {
        useToast().error("Falha ao buscar comparativo de faturamento");
      } finally {
        state.chartLoaded = true;
      }
    };

    const handleTipoChange = () => {
      state.referencia = referenciaAtual(state.periodoTipo);
      executeBuscaComparativoFaturamento();
    };

    onMounted(() => {
      executeBuscaComparativoFaturamento();
    });

    return {
      state,
      periodoOptions,
      opcoesInstancia,
      amIAllowed,
      handleTipoChange,
      executeBuscaComparativoFaturamento
    };
  }
});
</script>

<!-- Add "scoped" attribute to limit CSS to this component only -->
<style scoped lang="scss">
.comparativo-faturamento {
  margin-top: $space-4;
}

.comparativo-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.comparativo-filtros {
  display: flex;
  align-items: center;
  gap: $space-2;
}

.comparativo-chart {
  height: 600px;
  margin-top: $space-3;

  :deep(.p-chart) {
    height: 100%;
  }

  :deep(canvas) {
    height: 100% !important;
    width: 100% !important;
  }
}

.comparativo-vazio {
  color: $text-muted;
}
</style>
